import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Calendar,
  User,
  CreditCard,
  FileText,
  AlertCircle,
  Building,
  Pencil,
  Route,
  Globe,
  CalendarPlus,
  Hash,
} from "lucide-react";
import { getBookingStatusDisplay } from "@/lib/constants/statuses";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";
import { format } from "date-fns";

interface BookingInfoCardProps {
  booking: any;
  bookingDetails: any;
  bookingStatuses: any[];
  paymentStatuses: any[];
  onEditBookingStatus: () => void;
  onEditPaymentStatus: () => void;
  onEditHost: () => void;
  onEditPaymentMethod: () => void;
  onEditDateTime?: () => void;
  onEditLanguage?: () => void;
  getStatusColor: (status: string, statusId?: number) => string;
  getPaymentIcon: (method: string) => React.JSX.Element;
  canEdit: boolean;
  canViewPaymentDetails: boolean;
}

export function BookingInfoCard({
  booking,
  bookingDetails,
  bookingStatuses,
  paymentStatuses,
  onEditBookingStatus,
  onEditPaymentStatus,
  onEditHost,
  onEditPaymentMethod,
  onEditDateTime,
  onEditLanguage,
  getStatusColor,
  getPaymentIcon,
  canEdit,
  canViewPaymentDetails,
}: BookingInfoCardProps) {
  // Get all host bookings and sort by time to find the earliest
  const hostBookings = booking.studio_show_host_bookings || [];
  const sortedHostBookings = [...hostBookings].sort((a, b) => {
    const dateA = a.show_session_timeslot ? new Date(a.show_session_timeslot).getTime() : 0;
    const dateB = b.show_session_timeslot ? new Date(b.show_session_timeslot).getTime() : 0;
    return dateA - dateB; // Ascending order (earliest first)
  });

  // Use the earliest show for display
  const hostBooking = sortedHostBookings[0];

  // Determine booking type: Check booking_type_id from studio_show_host_bookings first
  let bookingType = "Private"; // default
  let bookingIcon = <User className="h-5 w-5 text-gray-600" />;

  if (hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD) {
    // Show the road-show event name (snapshotted in details), else "Road show".
    let eventLabel = "Road show";
    try {
      const parsed =
        typeof booking.details === "string"
          ? JSON.parse(booking.details)
          : booking.details;
      if (parsed?.eventTypeLabel) eventLabel = parsed.eventTypeLabel;
    } catch {
      // details isn't JSON — ignore.
    }
    bookingType = eventLabel;
    bookingIcon = <Route className="h-5 w-5 text-gray-600" />;
  } else if (booking.customer_type_id === 2) {
    bookingType = "Corporate";
    bookingIcon = <Building className="h-5 w-5 text-gray-600" />;
  }

  const paymentMethod = booking.payment_method || "Not specified";

  return (
    <Card className="py-0">
      <div className="p-4">
        <h2 className="text-lg font-semibold mb-3">Booking information</h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Booking Number */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Hash className="h-5 w-5 text-gray-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Booking number</p>
                <p className="font-medium text-sm">
                  {booking.id || "Not available"}
                </p>
              </div>
            </div>
          </div>

          {/* Booking Type */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                {bookingIcon}
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">
                  {hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD
                    ? "Event type"
                    : "Booking type"}
                </p>
                <p className="font-medium text-sm">{bookingType}</p>
              </div>
            </div>
          </div>

          {/* Show Date (Arrival Date) */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-gray-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Event date</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {bookingDetails?.showDate
                        ? format(new Date(bookingDetails.showDate), "dd MMM yyyy")
                        : "Not available"}
                    </p>
                  </div>
                  {canEdit && onEditDateTime && hostBooking?.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onEditDateTime}
                      className="h-6 w-6 p-0"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Booking Status */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-gray-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Booking status</p>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={`${getStatusColor(booking.booking_status?.name, booking.status_id)} border`}
                  >
                    {getBookingStatusDisplay(
                      booking.booking_status?.name,
                      booking.status_id,
                    )}
                  </Badge>
                  {canEdit && bookingStatuses.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onEditBookingStatus}
                      className="h-6 w-6 p-0"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Host Name */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Host name</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">
                    {bookingDetails?.hostName || "Not assigned"}
                  </p>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onEditHost}
                      className="h-6 w-6 p-0"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          {canViewPaymentDetails && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-3">
                <CreditCard className="h-5 w-5 text-gray-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Payment status</p>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`${getStatusColor(booking.payment_status?.name, booking.payment_status_id)} border`}
                    >
                      {booking.payment_status?.name || "Not Paid"}
                    </Badge>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onEditPaymentStatus}
                        className="h-6 w-6 p-0"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Preference */}
          {canViewPaymentDetails && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-gray-600">
                  {getPaymentIcon(booking.payment_method || "card")}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">
                    Payment preference
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm capitalize">
                      {paymentMethod}
                    </p>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onEditPaymentMethod}
                        className="h-6 w-6 p-0"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Language */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-gray-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Language</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm uppercase">
                    {booking.lang || "EN"}
                  </p>
                  {canEdit && onEditLanguage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onEditLanguage}
                      className="h-6 w-6 p-0"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
